/**
 * No Tool Access Component
 *
 * Displays a user-friendly message when a user doesn't have sufficient
 * permissions to access a studio tool. Provides clear explanation and
 * actionable next steps.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  Stack,
} from '@mui/material';
import {
  Info as InfoIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import Header from '../layout/Header';

type StudioRole = 'owner' | 'manager' | 'admin';

interface NoToolAccessProps {
  /**
   * Name of the tool being accessed (e.g., "Team Bios", "Students")
   */
  toolName: string;
  /**
   * Required roles to access this tool
   */
  requiredRoles: StudioRole[];
  /**
   * Optional custom message to display instead of default
   */
  customMessage?: string;
}

/**
 * Component that displays when user lacks permissions to access a tool.
 * Shows clear messaging about requirements and provides navigation options.
 */
const NoToolAccess: React.FC<NoToolAccessProps> = ({
  toolName,
  requiredRoles,
  customMessage,
}) => {
  const navigate = useNavigate();

  // Format role names for display
  const formatRoles = (roles: StudioRole[]): string => {
    const formatted = roles.map(role => {
      switch (role) {
        case 'owner':
          return 'Owner';
        case 'manager':
          return 'Manager';
        case 'admin':
          return 'Admin';
        default:
          return role;
      }
    });

    if (formatted.length === 1) {
      return formatted[0];
    }
    if (formatted.length === 2) {
      return `${formatted[0]} or ${formatted[1]}`;
    }
    const lastRole = formatted.pop();
    return `${formatted.join(', ')}, or ${lastRole}`;
  };

  const rolesText = formatRoles(requiredRoles);

  return (
    <>
      <Header />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'info.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InfoIcon sx={{ fontSize: 48, color: 'info.main' }} />
          </Box>

          {/* Title */}
          <Typography variant="h4" component="h1" align="center">
            Permission Required
          </Typography>

          {/* Main Message */}
          <Alert severity="info" sx={{ width: '100%' }}>
            {customMessage || (
              <>
                <Typography variant="body1" gutterBottom>
                  <strong>
                    You need {rolesText} permissions to access {toolName}.
                  </strong>
                </Typography>
                <Typography variant="body2">
                  You currently don&apos;t have any studios where you have these
                  permissions.
                </Typography>
              </>
            )}
          </Alert>

          {/* Next Steps */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              What you can do:
            </Typography>
            <Stack spacing={1} sx={{ ml: 2 }}>
              <Typography variant="body2">
                • Contact your studio owner or manager to request elevated
                permissions
              </Typography>
              <Typography variant="body2">
                • Create your own studio as the owner
              </Typography>
            </Stack>
          </Box>

          {/* Action Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ width: '100%', mt: 2 }}
          >
            <Button
              variant="contained"
              startIcon={<BusinessIcon />}
              onClick={() => navigate('/studios')}
              fullWidth
            >
              Go to Studios
            </Button>
            <Button
              variant="outlined"
              startIcon={<DashboardIcon />}
              onClick={() => navigate('/dashboard')}
              fullWidth
            >
              Go to Dashboard
            </Button>
          </Stack>
        </Paper>
      </Container>
    </>
  );
};

export default NoToolAccess;
